import { useEffect, useLayoutEffect, useRef } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components-ui/AlertDialog';
import { firstFocusableOpener, useModalReturnFocus } from '../../components-ui/useModalReturnFocus';
import Button from '../Button';

type HasUnsavedChangesDialogProps = {
  open: boolean;
  cancelNavigation: () => void;
  confirmNavigation: () => void;
};

// asChild: each answer is the button itself (no nested button), sized as before (no stretch, no Cancel margin).
// The legacy Button draws no focus at all, and its border is a box-shadow, so keyboard focus is an outline in the DS
// focus green. Focused, it paints in one frame (ButtonBase transitions `all`) and above its stacked neighbour.
const ANSWER =
  'mt-0 self-center focus-visible:relative focus-visible:z-[1] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-400 focus-visible:transition-none';

const asElement = (target: EventTarget | null) => (target instanceof HTMLElement ? target : null);

const HasUnsavedChangesDialog = ({ open, cancelNavigation, confirmNavigation }: HasUnsavedChangesDialogProps) => {
  const { capture, onCloseAutoFocus } = useModalReturnFocus();
  const lastBlurred = useRef<HTMLElement | null>(null);
  // The legacy ButtonBase (e.g. the settings tabs) blurs itself before its handler opens the guard.
  useEffect(() => {
    const remember = (event: FocusEvent) => {
      lastBlurred.current = asElement(event.target);
    };
    document.addEventListener('focusout', remember, true);
    return () => document.removeEventListener('focusout', remember, true);
  }, []);
  // Before the guard takes the focus: the control that tried to leave gets it back when the guard closes.
  useLayoutEffect(() => {
    if (open) capture(firstFocusableOpener(asElement(document.activeElement), lastBlurred.current));
  }, [open, capture]);

  return (
    <AlertDialog open={open}>
      {/* Escape answers "Continue editing"; Radix focuses that Cancel on open. */}
      <AlertDialogContent onEscapeKeyDown={cancelNavigation} onCloseAutoFocus={onCloseAutoFocus}>
        <AlertDialogHeader>
          <AlertDialogTitle>Unsaved changes</AlertDialogTitle>
          <AlertDialogDescription>
            <span className="mt-1 text-sm font-normal leading-5 text-neutral-400">
              You have unsaved changes, are you sure you want to leave this page? Changes you made will not be saved.
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel asChild className={ANSWER}>
            <Button onClick={cancelNavigation} variant="contained" color="secondary" size="small">
              Continue editing
            </Button>
          </AlertDialogCancel>

          <AlertDialogAction asChild className={ANSWER}>
            <Button onClick={confirmNavigation} variant="contained" color="danger" size="small">
              Leave page
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default HasUnsavedChangesDialog;
